"use client";

import {
  useState,
  useRef,
  useEffect,
  ClipboardEvent,
  KeyboardEvent,
  ReactNode,
} from "react";
import { Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

const MIN_HEIGHT = 42;
const MAX_HEIGHT = 128;

export interface ChatInputProps {
  onSend: (message: string) => void | Promise<void>;
  placeholder?: string;
  disabled?: boolean;
  isLoading?: boolean;
  className?: string;
  prefill?: { token: number; text: string } | null;
  aboveSlot?: ReactNode;
  leadingSlot?: ReactNode;
  onPaste?: (event: ClipboardEvent<HTMLTextAreaElement>) => void;
  allowEmptyMessage?: boolean;
}

export function ChatInput({
  onSend,
  placeholder = "Type your message...",
  disabled = false,
  isLoading = false,
  className,
  prefill = null,
  aboveSlot,
  leadingSlot,
  onPaste,
  allowEmptyMessage = false,
}: ChatInputProps) {
  const [message, setMessage] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!prefill) return;
    setMessage(prefill.text);
    textareaRef.current?.focus();
  }, [prefill?.token]);

  const adjustHeight = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    const newHeight = Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, el.scrollHeight));
    el.style.height = `${newHeight}px`;
  };

  useEffect(() => {
    adjustHeight();
  }, [message]);

  const canSend = Boolean(message.trim()) || allowEmptyMessage;

  const handleSend = async () => {
    if (!canSend || disabled || isLoading) return;

    const messageToSend = message.trim();
    setMessage("");
    const el = textareaRef.current;
    if (el) {
      el.style.height = `${MIN_HEIGHT}px`;
    }
    await onSend(messageToSend);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {aboveSlot}
      <div className="flex items-center gap-[9px]">
        {leadingSlot}
        <Textarea
          ref={textareaRef}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={onPaste}
          placeholder={placeholder}
          disabled={disabled}
          className="max-h-32 min-h-[42px] resize-none overflow-y-auto rounded-[21px] border-iai-border px-4 py-[11px] text-sm shadow-none focus-visible:border-iai-purple focus-visible:ring-0 focus-visible:ring-offset-0 [scrollbar-width:none] [&::-webkit-scrollbar]:h-0 [&::-webkit-scrollbar]:w-0"
          rows={1}
          style={{ height: MIN_HEIGHT }}
        />
        <Button
          onClick={handleSend}
          disabled={!canSend || disabled || isLoading}
          size="icon"
          className="size-[38px] shrink-0 rounded-full bg-iai-purple text-white shadow-none hover:bg-iai-purple/90"
        >
          <Send className="size-4" strokeWidth={2.2} />
          <span className="sr-only">Send message</span>
        </Button>
      </div>
    </div>
  );
}
